import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Radio, MessageCircle, Sparkles, DollarSign, ShoppingBag, Zap, ArrowRight } from "lucide-react";

/**
 * FeatureCinema — bold, alternating cinematic feature strip. Each row is
 * an oversized display heading on one side, a background photo panel on
 * the other. Reveals on scroll.
 */
const FEATURES = [
  {
    id: "radio",
    kicker: "02 / Free radio",
    title: "The whole world tunes in.",
    body: "Every artist's top track lands on the trending station automatically. No login. No paywall. Just the loudest room online.",
    cta: { label: "Play the radio", to: "/browse" },
    icon: Radio,
    img: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&w=1200&q=70",
    accent: "#FF4500",
  },
  {
    id: "master",
    kicker: "03 / AI Mastering",
    title: "Studio-loud, in one click.",
    body: "Upload a rough mix. Get it back mastered against a reference, ready for the vault. Free with your artist subscription — unlimited.",
    cta: { label: "See how it works", to: "/manual" },
    icon: Sparkles,
    img: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?crop=entropy&cs=srgb&fm=jpg&w=1200&q=70",
    accent: "#FF4500",
  },
  {
    id: "keep",
    kicker: "04 / You keep 100%",
    title: "Every dollar. Every time.",
    body: "Sales go through your own Stripe account — MeloCraft doesn't touch a cent. All we ask is $2 a month to keep the lights on.",
    cta: { label: "Start selling", to: "/register" },
    icon: DollarSign,
    img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&w=1200&q=70",
    accent: "#FF4500",
  },
  {
    id: "chat",
    kicker: "05 / The Green Room",
    title: "One chat. Every artist.",
    body: "Drop tracks, hype a release, get feedback from other artists in real time. AI-moderated so it stays a signal room — not a spam feed.",
    cta: { label: "Enter the room", to: "/chat" },
    icon: MessageCircle,
    img: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?crop=entropy&cs=srgb&fm=jpg&w=1200&q=70",
    accent: "#FF4500",
  },
];

export default function FeatureCinema() {
  return (
    <section data-testid="feature-cinema" className="relative bg-[#0A0A0A] border-b border-[#2A2A2A]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-14"
        >
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500] mb-2">
            The whole stack
          </div>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.9] max-w-3xl">
            Everything you need<br />
            <span className="text-gray-500">to run the record label yourself.</span>
          </h2>
        </motion.div>

        <div className="space-y-16 md:space-y-24">
          {FEATURES.map((f, i) => (
            <FeatureRow key={f.id} f={f} flip={i % 2 === 1} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureRow({ f, flip, idx }) {
  const Icon = f.icon;
  return (
    <div
      data-testid={`feature-row-${f.id}`}
      className={`grid md:grid-cols-2 gap-8 md:gap-14 items-center ${flip ? "md:[direction:rtl]" : ""}`}
    >
      {/* Image side */}
      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 40 }}
        whileInView={{ opacity: 1, scale: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="relative aspect-[4/5] overflow-hidden border border-[#2A2A2A] [direction:ltr]"
      >
        <img
          src={f.img}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover"
        />
        {/* Cinematic overlays */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-transparent to-black/70" />
        <div
          className="absolute inset-0 mix-blend-multiply"
          style={{ background: `linear-gradient(135deg, ${f.accent}30, transparent 60%)` }}
        />
        {/* Big number */}
        <div className="absolute top-4 left-4 font-display font-black text-6xl md:text-8xl text-[#FF4500]/80 leading-none tracking-tighter">
          {String(idx + 1).padStart(2, "0")}
        </div>
        {/* Bottom badge */}
        <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-white bg-black/60 backdrop-blur px-3 py-2 border border-white/10">
          <Icon className="h-3.5 w-3.5 text-[#FF4500]" />
          {f.kicker}
        </div>
      </motion.div>

      {/* Text side */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        className="[direction:ltr]"
      >
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500] mb-3 flex items-center gap-2">
          <Icon className="h-4 w-4" /> {f.kicker}
        </div>
        <h3 className="font-display font-black text-3xl md:text-5xl tracking-tighter leading-[0.95] mb-5">
          {f.title}
        </h3>
        <p className="font-mono text-sm text-gray-300 leading-relaxed max-w-lg">{f.body}</p>
        <Link to={f.cta.to} className="mt-6 inline-flex group items-center gap-2 border border-white/20 hover:border-[#FF4500] hover:text-[#FF4500] px-5 py-3 font-mono text-xs uppercase tracking-widest transition-colors">
          {f.cta.label}
          <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </motion.div>
    </div>
  );
}
